import React from 'react'
import {
    Box,
    Card,
    CardActionArea,
    CardContent,
    Chip,
    CircularProgress,
    IconButton,
    Tooltip,
    Typography,
    makeStyles,
    Theme,
} from '@material-ui/core'
import ArchiveIcon from '@material-ui/icons/Archive'
import EditIcon from '@material-ui/icons/Edit'
import EventNoteIcon from '@material-ui/icons/EventNote'
import UnarchiveIcon from '@material-ui/icons/Unarchive'

import { LeasingSeasonListItem } from '../types/leasingSeason.types'
import { formatSeasonDateRange } from '../utils/seasonDates'
import { SeasonLeaseUpBar } from '../Overview/SeasonLeaseUpBar'

const useStyles = makeStyles((theme: Theme) => ({
    card: {
        transition: 'box-shadow 0.2s ease, transform 0.2s ease',
        '&:hover': {
            boxShadow: theme.shadows[4],
            transform: 'translateY(-1px)',
        },
    },
    archivedCard: {
        opacity: 0.85,
    },
    cardContent: {
        padding: theme.spacing(2.5, 3),
        '&:last-child': {
            paddingBottom: theme.spacing(2.5),
        },
    },
    mainRow: {
        display: 'flex',
        alignItems: 'flex-start',
        gap: theme.spacing(2),
    },
    iconWrap: {
        width: 44,
        height: 44,
        borderRadius: theme.spacing(1),
        backgroundColor: theme.palette.primary.main + '14',
        color: theme.palette.primary.main,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        marginTop: theme.spacing(0.25),
    },
    body: {
        flex: 1,
        minWidth: 0,
    },
    headerRow: {
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        gap: theme.spacing(1),
        marginBottom: theme.spacing(1.5),
    },
    titleBlock: {
        minWidth: 0,
    },
    seasonName: {
        fontWeight: 600,
        fontSize: '1.05rem',
        lineHeight: 1.3,
    },
    dateRange: {
        fontSize: '0.8rem',
        color: theme.palette.text.secondary,
        marginTop: theme.spacing(0.5),
    },
    chipRow: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: theme.spacing(0.75),
        marginTop: theme.spacing(1),
    },
    currentChip: {
        height: 22,
        backgroundColor: theme.palette.primary.main + '18',
        color: theme.palette.primary.main,
        fontWeight: 600,
        fontSize: '0.7rem',
    },
    archivedChip: {
        height: 22,
        fontSize: '0.7rem',
    },
    goalChip: {
        height: 22,
        fontSize: '0.7rem',
    },
    actionButtons: {
        display: 'flex',
        alignItems: 'center',
        flexShrink: 0,
        marginRight: theme.spacing(-0.5),
    },
    actionButton: {
        padding: theme.spacing(0.75),
    },
    percentLeased: {
        fontWeight: 700,
        fontSize: '1rem',
        fontVariantNumeric: 'tabular-nums',
    },
    progressLoading: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: 48,
    },
}))

interface SeasonListCardProps {
    season: LeasingSeasonListItem
    percentLeased?: number | null
    expectedPercent?: number | null
    lastYearPercent?: number | null
    isProgressLoading?: boolean
    archiveActionsDisabled?: boolean
    onOpen: () => void
    onEdit: () => void
    onArchive?: () => void
    onUnarchive?: () => void
}

export const SeasonListCard: React.FC<SeasonListCardProps> = ({
    season,
    percentLeased = 0,
    expectedPercent = null,
    lastYearPercent = null,
    isProgressLoading = false,
    archiveActionsDisabled = false,
    onOpen,
    onEdit,
    onArchive,
    onUnarchive,
}) => {
    const classes = useStyles()
    const dateRange = formatSeasonDateRange(season.start_date, season.end_date)
    const isArchived = Boolean(season.is_archived)

    return (
        <Card
            className={`${classes.card}${
                isArchived ? ` ${classes.archivedCard}` : ''
            }`}
            variant="outlined"
        >
            <CardActionArea onClick={onOpen}>
                <CardContent className={classes.cardContent}>
                    <Box className={classes.mainRow}>
                        <Box className={classes.iconWrap}>
                            <EventNoteIcon fontSize="small" />
                        </Box>

                        <Box className={classes.body}>
                            <Box className={classes.headerRow}>
                                <Box className={classes.titleBlock}>
                                    <Typography className={classes.seasonName}>
                                        {season.name}
                                    </Typography>
                                    {dateRange && (
                                        <Typography
                                            className={classes.dateRange}
                                        >
                                            {dateRange}
                                        </Typography>
                                    )}
                                    <Box className={classes.chipRow}>
                                        {season.is_current && (
                                            <Chip
                                                size="small"
                                                label="Current season"
                                                className={classes.currentChip}
                                            />
                                        )}
                                        {isArchived && (
                                            <Chip
                                                size="small"
                                                label="Archived"
                                                variant="outlined"
                                                className={classes.archivedChip}
                                            />
                                        )}
                                        <Chip
                                            size="small"
                                            label={`Goal ${season.goal_percent}%`}
                                            variant="outlined"
                                            className={classes.goalChip}
                                        />
                                    </Box>
                                </Box>

                                <Box className={classes.actionButtons}>
                                    {!isArchived &&
                                        !season.is_current &&
                                        onArchive && (
                                            <Tooltip title="Archive season">
                                                <span>
                                                    <IconButton
                                                        className={
                                                            classes.actionButton
                                                        }
                                                        size="small"
                                                        aria-label="Archive season"
                                                        disabled={
                                                            archiveActionsDisabled
                                                        }
                                                        onClick={(event) => {
                                                            event.stopPropagation()
                                                            onArchive()
                                                        }}
                                                    >
                                                        <ArchiveIcon fontSize="small" />
                                                    </IconButton>
                                                </span>
                                            </Tooltip>
                                        )}
                                    {isArchived && onUnarchive && (
                                        <Tooltip title="Restore season">
                                            <span>
                                                <IconButton
                                                    className={
                                                        classes.actionButton
                                                    }
                                                    size="small"
                                                    aria-label="Restore season"
                                                    disabled={
                                                        archiveActionsDisabled
                                                    }
                                                    onClick={(event) => {
                                                        event.stopPropagation()
                                                        onUnarchive()
                                                    }}
                                                >
                                                    <UnarchiveIcon fontSize="small" />
                                                </IconButton>
                                            </span>
                                        </Tooltip>
                                    )}
                                    {!isArchived && (
                                        <IconButton
                                            className={classes.actionButton}
                                            size="small"
                                            aria-label="Edit season"
                                            onClick={(event) => {
                                                event.stopPropagation()
                                                onEdit()
                                            }}
                                        >
                                            <EditIcon fontSize="small" />
                                        </IconButton>
                                    )}
                                </Box>
                            </Box>

                            {isProgressLoading ? (
                                <Box className={classes.progressLoading}>
                                    <CircularProgress size={22} />
                                </Box>
                            ) : (
                                <>
                                    <Box mb={0.5}>
                                        <Typography
                                            className={classes.percentLeased}
                                        >
                                            {(percentLeased ?? 0).toFixed(1)}%
                                            leased
                                        </Typography>
                                    </Box>
                                    <Box width="100%" position="relative">
                                        <SeasonLeaseUpBar
                                            percentLeased={percentLeased ?? 0}
                                            expectedPercent={expectedPercent}
                                            lastYearPercent={lastYearPercent}
                                            compact
                                        />
                                    </Box>
                                </>
                            )}
                        </Box>
                    </Box>
                </CardContent>
            </CardActionArea>
        </Card>
    )
}
